import { faRepeat } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { clsx } from 'clsx';
import type { CurrentUser } from 'hooks/useCurrentUser';
import { useId, useState } from 'react';
import { Link } from 'turnip/Link/Link';
import { routes } from 'utils/routes';
import { itemLinkClasses, submenuButtonClasses } from '../Submenu/classes';

function AccountSwitcher({
  accounts,
  activeAccountId,
}: {
  accounts: CurrentUser['accounts'];
  activeAccountId: number | undefined;
}) {
  const [filter, setFilter] = useState('');
  const accountsListId = useId();

  const maxVisibleAccounts = 10;
  const nonAgencyAccounts = accounts.filter((account) => !account.isAgency);
  const filteredAndLimitedAccounts = nonAgencyAccounts
    .filter((account) =>
      account.name?.toLowerCase()?.includes(filter.toLowerCase())
    )
    .slice(0, maxVisibleAccounts);

  return (
    <Popover>
      {({ open }) => (
        <>
          <PopoverButton
            className={clsx(
              submenuButtonClasses(open),
              'border-l-2 border-solid border-[#ee8408] px-4'
            )}
          >
            <span className="sr-only">Open account switcher</span>
            <FontAwesomeIcon icon={faRepeat} className="fa-width-auto" />
          </PopoverButton>

          <PopoverPanel
            className="absolute z-30 min-w-64 bg-white shadow-md"
            aria-label="Account switcher"
          >
            {nonAgencyAccounts.length >= maxVisibleAccounts && (
              <>
                <p className="px-2.5 pt-1.5 text-sm text-gray-700">
                  Showing {filteredAndLimitedAccounts.length} of{' '}
                  {nonAgencyAccounts.length} accounts
                </p>

                <label className="block px-2.5 py-1.5">
                  <input
                    type="search"
                    placeholder="Search Accounts"
                    name="filter"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="w-full border border-[#ccc] text-[#555] focus:border-orange-500 focus:ring-0"
                    aria-controls={accountsListId}
                  />
                  <span className="sr-only">Filter</span>
                </label>
              </>
            )}

            <ul id={accountsListId} aria-label="Accounts" aria-live="polite">
              {filteredAndLimitedAccounts.map(({ id, name }) => {
                const isActiveAccount = id === activeAccountId;

                return (
                  <li key={id}>
                    <Link
                      to={routes(id).switchAccount}
                      className={clsx(
                        itemLinkClasses(false, isActiveAccount),
                        'focus:text-orange-700'
                      )}
                      aria-current={isActiveAccount}
                    >
                      {name}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
}

export { AccountSwitcher };
